/**
 * @module ArtefactTagList
 * @purpose Renders artefact names as inline tags, with overflow truncation and modal expansion
 * @depends ArtefactListModal, react-i18next, ArtefactConfig type
 * @dependedOnBy OrgTemplateCard, VelociraptorTemplateStep
 *
 * @rules
 * - State: local useState for modal open/close only
 * - Performance: renders at most ARTEFACT_INLINE_THRESHOLD tags inline
 * - Errors: no API calls, display-only
 */

import { useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type { ArtefactConfig } from '../../types/velociraptor'
import { ArtefactListModal } from './ArtefactListModal'

const ARTEFACT_INLINE_THRESHOLD = 5
const ARTEFACT_TRUNCATED_DISPLAY_COUNT = 3

interface ArtefactTagListProps {
  artefacts: ArtefactConfig[]
  templateName: string
}

const TAG_CLASS =
  'inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-xs font-medium text-slate-600 dark:text-slate-300'

export function ArtefactTagList({ artefacts, templateName }: ArtefactTagListProps): ReactElement {
  const { t } = useTranslation('cases')
  const [modalOpen, setModalOpen] = useState(false)

  if (artefacts.length === 0) {
    return (
      <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
        {t('velociraptor.artefactList.noArtefacts', 'No artefacts configured')}
      </p>
    )
  }

  const showAll = artefacts.length <= ARTEFACT_INLINE_THRESHOLD
  const visibleArtefacts = showAll
    ? artefacts
    : artefacts.slice(0, ARTEFACT_TRUNCATED_DISPLAY_COUNT)
  const overflowCount = artefacts.length - ARTEFACT_TRUNCATED_DISPLAY_COUNT

  return (
    <>
      <div className="mt-2 flex flex-wrap gap-1">
        {visibleArtefacts.map((artefact) => (
          <span key={artefact.name} className={TAG_CLASS}>
            {artefact.name}
          </span>
        ))}
        {!showAll && (
          <button
            type="button"
            onClick={() => {
              setModalOpen(true)
            }}
            className={TAG_CLASS + ' cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-600'}
          >
            {t('velociraptor.artefactList.andMore', 'and {{count}} more', { count: overflowCount })}
          </button>
        )}
      </div>
      <ArtefactListModal
        artefacts={artefacts}
        templateName={templateName}
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false)
        }}
      />
    </>
  )
}
